/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

/**
 * @typedef {Object} MembershipGrant
 * @property {number|string} serviceId        Service the grant applies to (map key in the store).
 * @property {number|null}   clientMembershipId Purchased-membership instance paying for the service.
 * @property {string}        name             Membership name, shown in the applied-membership summary.
 * @property {number}        [rest]           Sessions remaining before this booking.
 * @property {number}        [used]           Sessions already used (so total = rest + used).
 * @property {boolean}       isUnlimited      Whether the membership has no session limit.
 * @property {string|null}   coverageStart    Coverage start date ('Y-m-d'); days before it can't be booked.
 * @property {string|null}   coverageEnd      Coverage end date ('Y-m-d'); days after it can't be booked.
 */

/**
 * Build the grant recorded when a membership unlocks a service. This is the
 * single source of the grant shape: the access modal builds it, the booking
 * slice stores it (keyed by serviceId), and the applied-membership summary reads it.
 *
 * @param {Object} service
 * @param {Object|null} purchase Purchased-membership instance, or null to record no membership.
 * @returns {MembershipGrant}
 */
export const createMembershipGrant = (service, purchase) => ({
  serviceId: service.id,
  clientMembershipId: purchase ? purchase.id : null,
  name: purchase?.membership?.name || "",
  rest: purchase?.rest,
  used: purchase?.count,
  isUnlimited: !!purchase?.membership?.is_unlimited,
  coverageStart: purchase?.period_start || null,
  coverageEnd: purchase?.period_end ? purchase.period_end.slice(0, 10) : null,
});

/**
 * Whether any of the services requires a membership to book.
 *
 * @param {Array<{memberships?: Array}>} services Selected services.
 * @returns {boolean}
 */
export const servicesRequireMembership = (services) =>
  (services || []).some((service) => (service.memberships || []).length > 0);

/**
 * Coverage intersection across the granted services, date-only ('Y-m-d'):
 * latest start / earliest end; null = unconstrained on that side.
 *
 * @param {Array<{id: number|string}>} services Selected services.
 * @param {Object<string, MembershipGrant>} grants membershipAccessGrants, keyed by serviceId.
 * @returns {{start: string|null, end: string|null}}
 */
export const getMembershipCoverageWindow = (services, grants) => {
  let start = null;
  let end = null;
  for (const service of services || []) {
    const grant = grants?.[service.id];
    if (!grant) continue;
    const s = grant.coverageStart ? grant.coverageStart.slice(0, 10) : null;
    if (s && (start === null || s > start)) start = s;
    if (grant.coverageEnd && (end === null || grant.coverageEnd < end)) end = grant.coverageEnd;
  }
  return { start, end };
};

/**
 * Earliest bookable date for the selection, or null when it's not in the future.
 *
 * @param {Array<{id: number|string}>} services Selected services.
 * @param {Object<string, MembershipGrant>} grants membershipAccessGrants, keyed by serviceId.
 * @param {string} today Today as 'Y-m-d'.
 * @returns {string|null}
 */
export const getMembershipCoverageMinDate = (services, grants, today) => {
  const { start } = getMembershipCoverageWindow(services, grants);
  return start && start > today ? start : null;
};

/**
 * Latest bookable date for the selection, or null when no grant carries one.
 *
 * @param {Array<{id: number|string}>} services Selected services.
 * @param {Object<string, MembershipGrant>} grants membershipAccessGrants, keyed by serviceId.
 * @returns {string|null}
 */
export const getMembershipCoverageMaxDate = (services, grants) =>
  getMembershipCoverageWindow(services, grants).end;

/**
 * True when the purchase's validity period shares at least one date with the
 * coverage window — i.e. booking it together with the current selection can succeed.
 *
 * @param {Object|null} purchase Purchased-membership instance (null = no constraint).
 * @param {{start: string|null, end: string|null}} coverage Existing coverage window.
 * @returns {boolean}
 */
export const isPurchaseDateCompatible = (purchase, { start, end }) => {
  const s = purchase?.period_start ? purchase.period_start.slice(0, 10) : null;
  const e = purchase?.period_end ? purchase.period_end.slice(0, 10) : null;
  return !(s && end && s > end) && !(e && start && e < start);
};
